/**
 * hydrateFromSettings.js — IDI-397: map existing tenant settings into the
 * onboarding wizard's field names so a migrated dealer sees their real data
 * pre-populated, not blank fields.
 *
 * Only hydrates fields that are still at their INITIAL_WIZARD_DATA value —
 * doesn't overwrite in-progress wizard edits from stepsData.
 *
 * The mapping is one-directional (settings → wizardData shape). The reverse
 * direction (wizardData → settings) is handled by buildSettingsPayload.js.
 */
import { normaliseLaborRates } from '../../quote-builder/laborMethods';
import { INITIAL_WIZARD_DATA, DEFAULT_CATEGORIES } from './onboardingInitialState';

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

// IDI-690 D1/D2: a saved Margins & Labor value with none of the dealer's own
// entries (an earlier run's untouched Continue) yields to Settings on resume.
const isBlank = (v) => v === '' || v == null;
const allBlank = (v) => (v != null && typeof v === 'object'
  ? Object.values(v).every(allBlank)
  : isBlank(v));

/** Returns a copy of the flattened saved steps without masterDefaultMargin,
 *  laborRates or categories where that key holds no dealer entry. Each key is
 *  judged on its own; a key with any entry is kept as saved. */
export function dropUntouchedMargins(saved) {
  const out = { ...(saved || {}) };
  if ('masterDefaultMargin' in out
    && (isBlank(out.masterDefaultMargin) || Number(out.masterDefaultMargin) === 5)) {
    delete out.masterDefaultMargin;
  }
  if ('laborRates' in out && allBlank(out.laborRates)) delete out.laborRates;
  if ('categories' in out && (out.categories == null
    || (Array.isArray(out.categories) && out.categories.every(c => !c || isBlank(c.margin))))) {
    delete out.categories;
  }
  // IDI-655 D6: a pre-change run's top-level tax fields are ignored.
  delete out.defaultTaxRate;
  delete out.taxRatesByState;
  delete out.storedTaxRates;
  // IDI-666 D5: a pre-change run's payment terms never reached Settings.
  delete out.paymentTerms;
  delete out.customPaymentDays;
  // IDI-626 D7: a pre-change run's whole T&C list is ignored; only its
  // saved changes (tcChanges) apply over Settings' entries.
  delete out.termsAndConditions;
  // IDI-657 D5: a pre-change run's whole crews / resources lists are
  // ignored; only setup's own adds (crewAdds, resourceAdds) are kept.
  delete out.crews;
  delete out.resources;
  delete out.storedCrews;
  // IDI-658 D3: a pre-change run's customers (browser-clock ids, never
  // created) are ignored; only setup's created {id, name} list is kept.
  delete out.customers;
  return out;
}

// IDI-656 D6: Your Business saves branding into Branding at Continue, so step
// progress keeps none; an earlier run's saved branding is ignored and
// Branding's current values show.
const SAVED_BRANDING_KEYS = [
  'branding', 'logo', 'logoUrl', 'logoMeta', 'brandColor', 'secondaryColor',
  'textColor', 'logoPosition', 'emailSignature', 'includeLogoInSignature',
];

export function dropSavedBranding(saved) {
  const out = { ...(saved || {}) };
  for (const k of SAVED_BRANDING_KEYS) delete out[k];
  return out;
}

/**
 * @param {object} settings — the raw getSettings() response
 * @param {object} currentWizardData — the wizard's current state (may have stepsData edits)
 * @returns {object} — fields to merge into wizardData (only non-initial values)
 */
export function hydrateFromSettings(settings, currentWizardData) {
  if (!settings || typeof settings !== 'object') return {};

  const hydrated = {};

  // Helper: only hydrate if the wizard field is still at its initial value.
  const setIfUntouched = (wizardKey, value) => {
    if (value == null || value === '') return;
    const initial = INITIAL_WIZARD_DATA[wizardKey];
    const current = currentWizardData[wizardKey];
    // For primitives: strict equality. For objects/arrays: JSON comparison
    // (matching buildSettingsPayload's touched() logic).
    const isUntouched = typeof initial === 'object'
      ? JSON.stringify(current) === JSON.stringify(initial)
      : current === initial;
    if (isUntouched) {
      hydrated[wizardKey] = value;
    }
  };

  // Step 1 — Company Profile
  const profile = settings.companyProfile || settings.company_profile || {};
  // IDI-654 D3: Company Profile's keys. The company phone fills Business
  // Phone only — About You's phone is the user's own (D2).
  setIfUntouched('companyName', profile.company_name || settings.company_name || profile.name);
  setIfUntouched('businessEmail', profile.email || settings.company_email);
  setIfUntouched('businessPhone', profile.phone || settings.phone);
  setIfUntouched('website', profile.website);
  if (profile.address_street || profile.street) {
    const addr = {
      street: profile.address_street || profile.street || '',
      city: profile.address_city || profile.city || '',
      state: profile.address_state || profile.state || '',
      zip: profile.address_zip || profile.zip || '',
    };
    if (addr.street || addr.city) {
      setIfUntouched('address', addr);
    }
  }

  // Step 2 — Branding. IDI-656 D4: Branding's snake_case keys only (old
  // camelCase copies are not read); the image is logo_url.
  const branding = settings.branding || {};
  setIfUntouched('brandColor', branding.primary_color);
  setIfUntouched('secondaryColor', branding.secondary_color);
  setIfUntouched('textColor', branding.text_color);
  setIfUntouched('logoUrl', branding.logo_url);
  if (branding.logo_url) setIfUntouched('logoMeta', branding.logo_meta);
  setIfUntouched('logoPosition', branding.logo_position);
  setIfUntouched('emailSignature', branding.email_signature);
  if (branding.include_logo_in_signature != null) {
    setIfUntouched('includeLogoInSignature', !!branding.include_logo_in_signature);
  }

  // Step 3 — Margins & Labor. IDI-397 D2: marginDefaults first, then
  // top-level; never `material`. `marginsShown` records what the step shows
  // so buildSettingsPayload writes only user changes (D3).
  const md = isPlainObject(settings.marginDefaults) ? settings.marginDefaults : {};
  const shown = {};
  // The baseline is what Settings holds even when a resumed wizard already
  // carries a saved Step 3 (which wins on screen), so an unchanged pre-fill
  // saved in an earlier session still writes nothing (F2).
  const show = (key, value) => {
    setIfUntouched(key, value);
    shown[key] = value;
  };
  const rawDefault = Number(md.master_default_margin ?? settings.master_default_margin ?? NaN);
  if (Number.isFinite(rawDefault)) {
    show('masterDefaultMargin', rawDefault > 1 ? rawDefault : Math.round(rawDefault * 10000) / 100);
  }
  // Labor rates: may be the #378 list shape — normaliseLaborRates handles both.
  const lr = md.labor_rates || settings.labor_rates;
  if (lr) {
    const normalised = normaliseLaborRates(lr);
    // Only hydrate if it has real values (not all-null).
    const hasValues = Object.values(normalised.piece || {}).some(v => v != null) ||
      normalised.hourly != null || normalised.day != null || normalised.set != null;
    if (hasValues) show('laborRates', normalised);
  }
  // Category tiles (D5): Settings' six plus visible custom categories, with
  // margins by Settings' keys.
  const cm = isPlainObject(md.category_margins) ? md.category_margins : {};
  const hidden = isPlainObject(md.category_visibility) ? md.category_visibility : {};
  const customs = (Array.isArray(md.custom_categories) ? md.custom_categories : [])
    .filter(c => c && c.key && hidden[c.key] !== false)
    .map(c => ({ id: c.key, name: c.label || c.key, margin: null, isCustom: true }));
  const tiles = [...DEFAULT_CATEGORIES, ...customs].map((c) => {
    const n = cm[c.id] === '' || cm[c.id] == null ? NaN : Number(cm[c.id]);
    return Number.isFinite(n) ? { ...c, margin: n } : c;
  });
  if (tiles.some((t, i) => t !== DEFAULT_CATEGORIES[i])) show('categories', tiles);
  if (Object.keys(shown).length > 0) hydrated.marginsShown = shown;

  // Step 4 — Tax & Defaults
  // IDI-655 D2/D6: Settings' Tax & Rates rows always show; saved progress
  // carries only typed changes (taxRateEdits), applied over them.
  if (Array.isArray(settings.taxRates) && settings.taxRates.length > 0) {
    hydrated.storedTaxRates = settings.taxRates;
  }
  // IDI-666 D3: Quote Defaults' term (display only, never saved as progress).
  const qdTerm = settings.quoteDefaults && settings.quoteDefaults.payment_terms;
  if (typeof qdTerm === 'string' && qdTerm) hydrated.storedPaymentTerm = qdTerm;
  // IDI-626 D3: Settings' T&C Templates entries always show (display only,
  // never saved as progress); setup's changes live in tcChanges.
  const tcs = settings.tcTemplates || settings.tc_templates;
  if (Array.isArray(tcs) && tcs.length > 0) hydrated.termsAndConditions = tcs;

  // Step 6 — Crews. IDI-657 D4: Settings' crews show read-only (display
  // only); setup's adds live in crewAdds.
  const crews = settings.crews;
  if (Array.isArray(crews) && crews.length > 0) {
    hydrated.storedCrews = crews.filter(c => c && c.active !== false);
  }

  return hydrated;
}
